(ns frontend.view.grid.surveys-grid.common-view
  (:require
   [common.attribute :as attribute]
   [common.fields.table-column-widths :as widths]
   [common.localization :as loc]
   [common.schema.registry :as registry]
   [common.teams :as teams]
   [frontend.db :as app-db]
   [frontend.local-storage :as ls]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.react.virtualized.scrollable-grid.common :as grid]
   [frontend.react.virtualized.scrollable-grid.components :as grid-components]
   [frontend.react.virtualized.scrollable-grid.kanban :as kanban]
   [frontend.react.virtualized.scrollable-grid.view :as scrollable-grid]
   [frontend.react.virtualized.utils :as vu]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.components.grid-filters.view :as grid-filters]
   [frontend.view.entity :as entity]
   [frontend.view.grid.common.components :as components]
   [frontend.view.grid.common.utils :as grid-utils]
   [frontend.view.grid.surveys-grid.db :as db]
   [frontend.view.grid.surveys-grid.events :as events]
   [frontend.view.grid.surveys-grid.mass-remind.view :as mass-remind-modal]
   [frontend.view.named-view.events :as named-view-events]
   [frontend.permissions.survey-mass-sending :as mass-sending]
   [potpuri.core :as potpuri]
   [reagent.core :as r]
   [ui.components.button.view :as button]))

(defonce select-width 30)

(defn column
  [args]
  (let [{:keys [label attribute field-schema filter-data sorting-disabled? width hover-atom
                recompute-grid-sizes! flex-grow]}
        args
        default-width (-> (widths/get-column-width attribute)
                          :default-width
                          (or widths/default-width))]
    {:label               label
     :data-key            attribute
     :field-schema        field-schema
     :filter-data         filter-data
     :update-filter!      (fn [k filter-data]
                            (grid/set-filter (events/setup-args) k filter-data))
     :sort-disabled?      (boolean sorting-disabled?)
     :min-width           (-> (widths/get-column-width attribute)
                              :min-width
                              (or widths/min-width))
     :max-width           (-> (widths/get-column-width attribute)
                              :max-width
                              (or widths/max-width))
     :default-width       default-width
     :width               (or width default-width)
     :set-width           width
     :begin-width-update! (partial grid/begin-width-update! hover-atom attribute)
     :update-width!
     (partial grid/update-width! (events/setup-args) recompute-grid-sizes! hover-atom attribute)
     :end-width-update!   (partial grid/end-width-update! hover-atom attribute)
     :get-styling         (partial grid/get-styling hover-atom attribute)
     :flex-grow           flex-grow}))

(defn toolbar-actions
  [{:keys [view-mode survey-type]}]
  (let [all-teams (->> @session/all-teams
                       (remove :common/disabled?))
        account-teams (teams/resolve-account-teams @session/account all-teams)]
    [{:toolbar-button?  true
      :dropdown-action? true
      :ord              2
      :hidden?          (fn []
                          (= view-mode :board-mode))
      :primary-button?  false
      :add-ellipsis?    true
      :on-select        (fn [_e]
                          (grid/open-select-columns-curtain (events/setup-args)))
      :label            (loc/tr :action/select-columns)}
     (grid-utils/export-button {:account-teams  account-teams
                                :button-ord     3
                                :grid-state     db/survey-profiles
                                :column-keys-fn events/resolve-column-keys})

     (when-not (session/is-read-only-user?)
       {:toolbar-button?  true
        :dropdown-action? true
        :ord              1
        :icon             :fa-plus
        :primary-button?  true
        :on-select        (fn [_e]
                            (let [custom-args {:start-value {:survey-profile/type (:db/id survey-type)}}]
                              (entity/open-creator! :entity.type/survey-profile custom-args)))
        :label            (if survey-type
                            (loc/tr :survey-profile-add-button-typed [(:survey-type/label survey-type)])
                            (loc/tr :survey-profile-add-button))})
     (when (= view-mode :board-mode)
       {:toolbar-button?  false
        :dropdown-action? true
        :ord              15
        :add-ellipsis?    true
        :label            (loc/tr :card-settings)
        :on-select        (fn [_e]
                            (events/open-card-settings!))})
     (grid-utils/preferred-landing-page-toggle {:view-id :surveys-grid
                                                :route-params {:id (:db/id survey-type)}})]))


(defn toolbar-buttons-renderer
  []
  [:div
   {:class "flex flex-row gap-2"}
   (if (session/is-read-only-user?)
     [:span (loc/tr :no-grid-actions-for-read-only-user)]
     [:<>
      (when (mass-sending/enabled?)
        [button/sm-secondary-solid
         {:on-click  events/mass-send-clicked}
         [:i.fal.fa-paper-plane {:aria-hidden true}]
         (loc/tr :send)])
      [button/sm-secondary-solid
       {:on-click events/mass-remind-clicked}
       [:i.fal.fa-paper-plane {:aria-hidden true}]
       (loc/tr :send-reminder)]
      [button/sm-secondary-solid
       {:on-click events/download-clicked
        :tooltip  (loc/tr :latest-attachments)}
       [:i.fal.fa-file {:aria-hidden true}]
       (loc/tr :attachments)]
      [components/toolbar-delete-button
       {:max-delete-count   20
        :selected-items     (grid/get-selected-items (events/setup-args))
        :on-delete-clicked! events/delete-selected-clicked}]])])


(defn create-props-atom
  [selected sorted-surveys filters grid-store recompute-grid-sizes! hover-atom survey-type]
  (let [tenant-schema @session/tenant-schema
        columns
        (vec (keep (fn [k]
                     (let [field-schema (registry/get-field-schema tenant-schema k)
                           label (attribute/attribute-label tenant-schema k)]
                       (column {:label                 label
                                :attribute             k
                                :field-schema          field-schema
                                :filter-data           (get filters k)
                                :sorting-disabled?     false
                                :width                 (grid/get-saved-width (events/setup-args) k)
                                :hover-atom            hover-atom
                                :recompute-grid-sizes! recompute-grid-sizes!
                                :flex-grow             1})))
                   (events/resolve-column-keys)))]
    (r/atom
     {:grid-store              grid-store
      :setup-args              (events/setup-args)
      :selected                selected
      :header-height           50
      :select-width            select-width
      :min-columns-width       30
      :height                  (- (or (:height @app-db/window) 400)
                                  75)                                                               ;; TODO: lazy - fixed 120px for button row in this view
      :avail-height            (- (or (:height @app-db/window) 400)
                                  75)                                                               ;; TODO: lazy - fixed 120px for button row in this view
      :useDynamicRowHeight     false                                                                ;; TODO: kebab-case
      :row-height              40                                                                   ;(fn [index] 40)
      :row-count               (if (sequential? sorted-surveys) (count sorted-surveys) 0)
      :scrollToIndex           0
      :columns                 columns
      :overscan-count          1
      :no-rows-renderer        (r/partial grid/default-no-rows-render
                                          @db/survey-profiles
                                          :entity.type/survey-profile
                                          {:create-fn
                                           (fn [_]
                                             (let [custom-args {:start-value {:survey-profile/type (:db/id survey-type)}}]
                                               (entity/open-creator! :entity.type/survey-profile custom-args)))})
      :on-row-click!           (fn [row]
                                 (let [data (when (< row (count sorted-surveys))
                                              (nth sorted-surveys row))]
                                   (swap! session/entities assoc (:db/id data) data)
                                   (routing/open-entity data)))
      :is-sorted-by?           (partial grid/is-sorted-by db/view-settings)
      :is-secondary-sorted-by? (partial grid/is-secondary-sorted-by db/view-settings)
      :sort-by!                (fn [k dir]
                                 (grid/set-sort-by (events/setup-args) k dir))
      :reorder-columns!        (fn [dragged-column after-column]
                                 (grid/reorder-columns! (events/setup-args)
                                                        dragged-column
                                                        after-column))
      :can-filter?             (fn [_k]
                                 true)
      :resizing-column         (some-> @hover-atom
                                       (get :resize-start-width)
                                       (first)
                                       (key))
      :recompute-grid-sizes!   recompute-grid-sizes!
      :all-selection!          (fn []
                                 (let [selected (:selected @db/survey-profiles)]
                                   (if (or (= :all selected) (seq selected))
                                     (grid/clear-selection! (events/setup-args))
                                     (swap! db/survey-profiles assoc :selected :all))))
      :select-action-renderer  toolbar-buttons-renderer
      :on-row-select!          (fn [index checked]
                                 (let [data (when (< index (count sorted-surveys))
                                              (nth sorted-surveys index))
                                       survey-profile-id (:db/id data)]
                                   (if (= selected :all)
                                     (grid/select-all-but (events/setup-args) survey-profile-id)
                                     (if (boolean checked)
                                       (swap! db/survey-profiles assoc-in
                                              [:selected survey-profile-id]
                                              true)
                                       (swap! db/survey-profiles potpuri/dissoc-in
                                              [:selected survey-profile-id])))))
      :row-data-getter         (r/partial grid/default-row-data-getter sorted-surveys selected)})))

(defn toolbar
  [{:keys [initial-mode survey-type only-grid?]}]
  (let [view-mode (cond only-grid?
                        :grid-mode
                        (or (:view-mode @db/view-settings)
                            (:view-mode (ls/get-in (events/ls-path))))
                        initial-mode
                        :else
                        :grid-mode)
        board-mode? (and (= view-mode :board-mode)
                         (not only-grid?))
        card-mode (or (:card-mode @db/kanban)
                      :short-card)
        has-empty-groups? (when board-mode?
                            (kanban/has-empty-groups? @(r/track events/setup-args)))]
    [:div
     {:class "px-4 py-1 flex items-center justify-between
                   bg-base-light
                   border-b border-solid border-base-light-border-paler"}
     [:div
      {:class "w-full flex justify-between gap-6"}
      [:div
       [:div
        {:class "flex gap-2 items-center"}
        [:div
         {:class "flex gap-2 items-center"}
         [:label
          {:class "font-semibold"}
          (if survey-type
            (loc/tr :surveys-grid-typed [(:survey-type/label survey-type)])
            (loc/tr :surveys-grid))]]

        [:span
         {:class "h-5 px-2 flex items-center justify-center text-xs font-semibold text-base-light-text bg-complement-regular rounded-full"}
         (count @db/sorted-kanban-entities)]

        [grid-components/named-view-selector
         {:named-views               (events/named-views)
          :current-view              @db/view-settings
          :load-view!                (fn [id]
                                       (events/load-view! (events/named-view-by-id id))
                                       (grid/clear-selection! {:state db/survey-profiles}))
          :delete-view!              named-view-events/start-delete-view!
          :accounts-with-view-access (session/active-accounts-with-module-access :module/survey)
          :save-current-view!        events/save-current-view!}]

        (when board-mode?
          [grid-components/sort-selector
           {:view-settings   @db/view-settings
            :entity-type     :entity.type/survey-profile
            :sortable-fields (events/resolve-column-key-universe)
            :set-sort-by!    (fn [k dir]
                               (grid/set-sort-by (events/setup-args) k dir))}])

        (when board-mode?
          [grid-components/grouped-by-selector
           {:view-settings      @db/view-settings
            :entity-type        :entity.type/survey-profile
            :default-grouped-by :survey-profile/status
            :grouped-by-fields  (->> (events/resolve-column-key-universe)
                                     (filter grid/groupable-attribute?))
            :set-grouped-by!    (fn [k]
                                  (swap! db/view-settings assoc :grouped-by k)
                                  (ls/update-in (events/ls-path)
                                                assoc
                                                :grouped-by
                                                k))}])]]
      [:div
       {:class "flex justify-content-end items-center gap-2"}
       (when (and board-mode? (not= card-mode :short-card))
         [button/square-secondary-open
          {:tooltip  (loc/tr :collapse-all)
           :on-click (fn [_] (events/collapse-all!))}
          [:i.fal.fa-compress-alt]])
       (when (and board-mode? (= card-mode :short-card))
         [button/square-secondary-open
          {:tooltip  (loc/tr :expand-all)
           :on-click (fn [_] (events/expand-all!))}
          [:i.fal.fa-expand-alt]])

       (when-not only-grid?
         [button/square-secondary-open
          {:tooltip (grid-components/view-mode-tooltip board-mode?)}
          {:on-click (fn [_]
                       (events/switch-mode! view-mode))}
          (if board-mode?
            [:i.fal.fa-table]
            [:i.fal.fa-signal-3.fa-rotate-180.kanban-icon])])

       (when (and board-mode? has-empty-groups?)
         [grid-utils/hide-empty-kanban-groups-toggle "survey-grid-toolbar" db/kanban])

       [:div
        (zap-toolbar/dropdown (->> (toolbar-actions {:view-mode   view-mode
                                                     :survey-type survey-type})
                                   (filter :dropdown-action?)
                                   (remove (fn [{:keys [hidden?]}]
                                             (when (fn? hidden?)
                                               (hidden?))))
                                   (sort-by :ord))
                              true)]]]]))

(defn render
  [grid-store survey-type-id _args]
  (let [hover-atom (r/atom {:hover {:col nil :row nil}})
        survey-type (session/get-entity survey-type-id)
        ;;for saving grid references:
        recompute-grid-sizes! (fn []
                                (js/setTimeout
                                 (partial vu/recompute-grid-sizes-with-state! grid-store)
                                 0))]
    (fn [args]
      (let [{:keys [view-mode]} (first args)
            tenant-schema @session/tenant-schema
            selected (:selected @db/survey-profiles)
            filters {}
            #_@db/survey-profile-filters                                                            ;;TODO later
            sorted-surveys (:sorted-grid-entities @db/survey-profiles)
            grid-atom (create-props-atom selected
                                         sorted-surveys
                                         filters
                                         grid-store
                                         recompute-grid-sizes!
                                         hover-atom
                                         survey-type)
            show-quick-filters? (:show-quick-filters? @db/survey-profiles)
            card-settings? (:card-settings? @db/kanban)
            setup-args @(r/track events/setup-args)
            filters-props {:minimized?            (not show-quick-filters?)
                           :hide-maximize?        true
                           :universal-search?     true
                           :search-placeholder    (loc/tr :search-for-a-survey-profile)
                           :setup-args            (events/setup-args)
                           :set-universal-search! (r/partial grid/set-universal-search! setup-args)
                           :set-name-search!      (r/partial grid/set-name-search! setup-args)
                           :hide-named-views?     true
                           :named-views           (events/named-views)
                           :current-view          @db/view-settings
                           :columns               (events/resolve-column-keys)
                           :all-columns           (events/resolve-column-key-universe)
                           :get-column-label      (fn [a]
                                                    (attribute/attribute-label @session/tenant-schema a))
                           :set-filter!           (fn [k filter-data]
                                                    (grid/set-filter (events/setup-args) k filter-data))
                           :edit-filter!          (fn [k filter-data]
                                                    (grid/open-filter-curtain
                                                     {:field-key      k
                                                      :field-schema   (registry/get-field-schema tenant-schema
                                                                                                 k)
                                                      :options        (->>
                                                                       (events/resolve-column-key-universe)
                                                                       (map (juxt identity
                                                                                  (partial
                                                                                   registry/get-field-schema
                                                                                   tenant-schema))))
                                                      :current-filter filter-data
                                                      :update-fn!     (fn [k filter-data]
                                                                        (grid/set-filter (events/setup-args)
                                                                                         k
                                                                                         filter-data))}
                                                     nil))
                           :hide-qf-attributes    [:asset/name]
                           :load-view!            (fn [named-view]
                                                    (events/load-view! named-view)
                                                    (grid/clear-selection! {:state db/survey-profiles}))
                           :save-view!            events/save-current-view!
                           :delete-view!          named-view-events/delete-view!
                           :toggle-minimize!      (fn []
                                                    (events/toggle-quick-filters!)
                                                    (.setTimeout js/window recompute-grid-sizes! 1000))}
            hiccup
            (if card-settings?
              [kanban/card-settings-view (events/setup-args)]
              [:<>
               [grid-filters/view
                filters-props]
               [:div
                {:class "flex flex-auto ps-4"}
                [:div
                 {:class-name (fu/cs "grid-view-content"
                                     (when-not show-quick-filters? "named-views-minimized")
                                     (when (= view-mode :board-mode) "board-view"))}
                 (if (= view-mode :board-mode)
                   [kanban/view
                    {:setup-args   (events/setup-args)
                     :card-actions [(kanban/open-entity-action
                                     {:entity-type :entity.type/survey-profile})]}]
                   [:<>
                    [mass-remind-modal/view]
                    [scrollable-grid/view
                     {:props-atom grid-atom
                      :hover-atom hover-atom}]])]]])]
        hiccup))))
